/* Admin · Data Governance — Hard Delete User tool + permanent audit ledger. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Search, ShieldAlert, Trash2, FileDown, Loader2, Lock, Database, Wallet, FileText,
  Receipt, MessageSquare, Boxes, CheckCircle2, AlertTriangle, X, ScrollText,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';
const RED = "#E5484D";
const CAT_ICON = { account: Lock, transactions: Receipt, rfq: MessageSquare, kyc: FileText, vcb: Wallet, other: Boxes };

export function AdminDataGovernance({ api }) {
  const [auditKey, setAuditKey] = useState(0);
  return (
    <div className="space-y-10" data-testid="data-governance">
      <div>
        <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Data Governance</div>
        <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-1">Hard Delete User</h2>
        <p className="font-body text-sm text-[#8A8A82] mt-1 max-w-2xl">
          Permanent, irreversible erasure of a user and every associated record across all data stores and file storage.
          No soft delete. No recovery. Every execution is sealed into the tamper-proof audit ledger with a Letter of Intimation.
        </p>
      </div>
      <TaxIdPolicyCard api={api} />
      <HardDeleteTool api={api} onDeleted={() => setAuditKey((k) => k + 1)} />
      <AuditLedger api={api} refreshKey={auditKey} />
    </div>
  );
}

function TaxIdPolicyCard({ api }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setP((await api.get("/admin/data-governance/tax-id-policy")).data); }
    catch (e) { toast.error("Could not load tax-id policy"); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const set = async (mode) => {
    if (!p || mode === p.mode || busy) return;
    setBusy(true);
    try {
      await api.put("/admin/data-governance/tax-id-policy", { mode });
      toast.success(mode === "masked" ? "Vinci GSTIN & PAN will print partially masked" : "Vinci GSTIN & PAN will not be printed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const OPTIONS = [
    ["masked", "Print masked", `GSTIN ${p?.gstin_masked || "2XXX…"} · PAN ${p?.pan_masked || "XX…"}`],
    ["omitted", "Do not print", "No Vinci GSTIN / PAN on any letter, report or letterhead"],
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7" data-testid="tax-id-policy-card">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-xl">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">Vinci tax identifiers on documents</div>
          <h3 className="font-serif text-xl text-[#F5F5F0] mt-1">GSTIN &amp; PAN print policy</h3>
          <p className="font-body text-xs text-[#8A8A82] mt-1">
            Applies to every letter, intimation, banker report, bank demand, monumental document and letterhead asset.
            Tax invoices always carry the locked partial mask and are never affected.
          </p>
        </div>
        <div className="flex gap-2" data-testid="tax-id-policy-options">
          {OPTIONS.map(([k, label, sub]) => {
            const active = p?.mode === k;
            return (
              <button key={k} data-testid={`tax-id-policy-${k}`} disabled={busy || !p} onClick={() => set(k)}
                      className={`text-left rounded-xl px-4 py-3 border transition-colors min-w-[220px] ${active ? "border-[#C9A961] bg-[#C9A961]/10" : "border-white/15 hover:border-[#B87333]"}`}>
                <div className="flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.22em]" style={{ color: active ? "#C9A961" : "#C9C9C2" }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: active ? "#C9A961" : "transparent", boxShadow: `inset 0 0 0 1px ${active ? "#C9A961" : "#5F5F58"}` }} />{label}
                </div>
                <div className="font-body text-[11px] text-[#8A8A82] mt-1" style={{ fontFamily: k === "masked" ? MONO : undefined }}>{sub}</div>
              </button>
            );
          })}
        </div>
      </div>
      {p?.updated_at && <div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#5F5F58] mt-4" data-testid="tax-id-policy-meta">Last set by {p.updated_by} · {new Date(p.updated_at).toLocaleString("en-IN")}</div>}
    </div>
  );
}

function HardDeleteTool({ api, onDeleted }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [presets, setPresets] = useState([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null);
  const [fp, setFp] = useState(null);
  const [loadingFp, setLoadingFp] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [addressedTo, setAddressedTo] = useState("");
  const [statement, setStatement] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setResults([]); return; }
      setSearching(true);
      try {
        const { data } = await api.get(`/admin/data-governance/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data.results || []); setPresets(data.presets || []);
      } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, api]);

  const pick = useCallback(async (email) => {
    setTarget(email); setFp(null); setDone(null); setConfirm(""); setLoadingFp(true);
    try {
      const { data } = await api.get(`/admin/data-governance/footprint?email=${encodeURIComponent(email)}`);
      setFp(data);
      if (!presets.length) { const r = await api.get("/admin/data-governance/search?q=xx"); setPresets(r.data.presets || []); }
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not load footprint"); setTarget(null); }
    finally { setLoadingFp(false); }
  }, [api, presets.length]);

  const reset = () => { setTarget(null); setFp(null); setReason(""); setNote(""); setAddressedTo(""); setStatement(""); setConfirm(""); setDone(null); };
  const canExecute = !!fp && reason.trim().length > 0 && confirm.trim().toLowerCase() === (target || "").toLowerCase() && !busy;

  const execute = async () => {
    if (!canExecute) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/data-governance/hard-delete", {
        email: target, reason: reason.trim(), reason_note: note.trim(), confirm_email: confirm.trim(),
        addressed_to: addressedTo.trim(), statement: statement.trim(),
      });
      setDone(data.entry); toast.success(`Hard delete sealed · ${data.entry.ref}`); onDeleted?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Hard delete failed");
    } finally { setBusy(false); }
  };

  if (done) return <DeletionReceipt entry={done} onReset={reset} />;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 space-y-7" data-testid="hard-delete-tool">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A82]" />
        <input data-testid="dg-search-input" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search by email, name or phone number…"
               className="w-full bg-black/40 border border-white/15 rounded-full pl-11 pr-11 py-3 font-body text-sm text-[#F5F5F0] placeholder:text-[#5F5F58] focus:outline-none focus:border-[#C9A961]" />
        {searching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9A961] animate-spin" />}
        {results.length > 0 && !target && (
          <ul data-testid="dg-search-results" className="absolute z-20 mt-2 w-full rounded-2xl border border-white/10 bg-[#0B0B0C] shadow-2xl divide-y divide-white/[0.06] overflow-hidden">
            {results.map((r) => (
              <li key={r.id}>
                <button data-testid={`dg-result-${r.id}`} onClick={() => { pick(r.email); setResults([]); }}
                        className="w-full text-left px-5 py-3 hover:bg-white/[0.04] flex items-center justify-between gap-4">
                  <div>
                    <div className="font-body text-sm text-[#F5F5F0]">{r.name || "—"} <span className="text-[#8A8A82]">· {r.email}</span></div>
                    <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] mt-0.5">{r.role} · {r.status}{r.phone ? ` · ${r.phone}` : ""}{r.company ? ` · ${r.company}` : ""}</div>
                  </div>
                  <ShieldAlert className="w-4 h-4 text-[#8A8A82]" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {q.includes("@") && results.length === 0 && !searching && !target && (
          <button data-testid="dg-use-email-literal" onClick={() => pick(q.trim().toLowerCase())}
                  className="mt-3 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9A961] hover:text-[#F5F5F0]">
            No account matched — inspect residual data for “{q.trim()}”
          </button>
        )}
      </div>

      {loadingFp && <div className="flex items-center gap-3 text-[#8A8A82] font-body text-sm"><Loader2 className="w-4 h-4 animate-spin text-[#C9A961]" /> Scanning every data store…</div>}

      {fp && (
        <>
          <Footprint fp={fp} onClear={reset} />
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Reason (mandatory)" testid="dg-reason-select">
              <select data-testid="dg-reason-select" value={reason} onChange={(e) => setReason(e.target.value)}
                      className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#C9A961]">
                <option value="">Select a reason…</option>
                {presets.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Reason note (optional)">
              <input data-testid="dg-reason-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Case ref, ticket, order no…"
                     className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#C9A961]" />
            </Field>
            <Field label="Letter addressed to (optional)">
              <input data-testid="dg-addressed-to" value={addressedTo} onChange={(e) => setAddressedTo(e.target.value)}
                     placeholder={fp.account?.name || fp.email}
                     className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#C9A961]" />
            </Field>
            <Field label="Additional statement in letter (optional)">
              <input data-testid="dg-statement" value={statement} onChange={(e) => setStatement(e.target.value)}
                     placeholder="e.g. The apology tendered has been accepted…"
                     className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#C9A961]" />
            </Field>
          </div>

          <div className="rounded-2xl border p-5 md:p-6 space-y-4" style={{ borderColor: "rgba(229,72,77,0.45)", background: "rgba(229,72,77,0.06)" }}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: RED }} />
              <div className="font-body text-sm text-[#F5F5F0]">
                Double verification. Type <span style={{ fontFamily: MONO, color: "#FFB3B5" }}>{fp.email}</span> exactly to arm the hard delete.
                This permanently destroys <b>{fp.records_total}</b> record(s) and scrubs <b>{fp.files_total}</b> file(s). There is no undo.
              </div>
            </div>
            <input data-testid="dg-confirm-email" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type the exact email address to confirm"
                   className="w-full bg-black/50 border rounded-xl px-4 py-3 font-body text-sm text-[#F5F5F0] focus:outline-none"
                   style={{ borderColor: confirm && confirm.trim().toLowerCase() === fp.email ? RED : "rgba(255,255,255,0.15)", fontFamily: MONO }} />
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="dg-execute-btn" disabled={!canExecute} onClick={execute}
                      className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-grotesk text-xs uppercase tracking-[0.22em] text-white disabled:opacity-30 disabled:cursor-not-allowed transition-[transform,opacity] hover:scale-[1.02]"
                      style={{ background: RED, boxShadow: canExecute ? "0 0 28px rgba(229,72,77,0.45)" : "none" }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Hard delete permanently
              </button>
              <button data-testid="dg-cancel-btn" onClick={reset} className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#F5F5F0]">Cancel</button>
              {!reason && <span className="font-grotesk text-[10px] uppercase tracking-[0.2em]" style={{ color: "#FFB3B5" }}>Reason required</span>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-2">
      <span className="font-grotesk text-[10px] uppercase tracking-[0.26em] text-[#8A8A82]">{label}</span>
      {children}
    </label>
  );
}

function Footprint({ fp, onClear }) {
  const a = fp.account;
  return (
    <div className="space-y-5" data-testid="dg-footprint">
      <div className="flex items-start justify-between gap-4 rounded-2xl border border-[#C9A961]/30 bg-[#C9A961]/[0.05] p-5">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.26em] text-[#C9A961]">Target identity</div>
          <div className="font-serif text-xl text-[#F5F5F0] mt-1" data-testid="dg-target-email">{a?.name || "No active account record"} <span className="text-[#8A8A82] font-body text-sm">· {fp.email}</span></div>
          {a ? (
            <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <span>Role · {a.role}</span><span>Status · {a.status}</span>{a.phone && <span>Phone · {a.phone}</span>}{a.company && <span>{a.company}</span>}
              {a.vcb_status && <span>VCB · {a.vcb_status}</span>}<span style={{ fontFamily: MONO }}>ID · {a.id}</span>
            </div>
          ) : <div className="font-body text-xs text-[#8A8A82] mt-2">Only residual records tied to this email will be purged.</div>}
        </div>
        <button data-testid="dg-clear-target" onClick={onClear} className="text-[#8A8A82] hover:text-[#F5F5F0]"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex items-baseline gap-6">
        <div><span className="font-serif text-3xl text-[#F5F5F0]" data-testid="dg-records-total">{fp.records_total}</span> <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">records</span></div>
        <div><span className="font-serif text-3xl text-[#F5F5F0]">{fp.collections_touched}</span> <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">data stores</span></div>
        <div><span className="font-serif text-3xl text-[#F5F5F0]" data-testid="dg-files-total">{fp.files_total}</span> <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">stored files</span></div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {fp.categories.map((c) => {
          const Icon = CAT_ICON[c.key] || Boxes;
          return (
            <div key={c.key} data-testid={`dg-cat-${c.key}`} className="rounded-xl border border-white/10 bg-black/30 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9C9C2]"><Icon className="w-3.5 h-3.5 text-[#C9A961]" />{c.label}</div>
                <span className="font-serif text-lg" style={{ color: c.total ? "#FFB3B5" : "#5F5F58" }}>{c.total}</span>
              </div>
              {c.collections.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {c.collections.map((r) => (
                    <li key={r.collection} className="flex justify-between font-body text-xs text-[#8A8A82]"><span style={{ fontFamily: MONO }}>{r.collection}</span><span>{r.count}</span></li>
                  ))}
                </ul>
              )}
              {!c.collections.length && <div className="mt-3 font-body text-xs text-[#5F5F58]">Nothing found</div>}
            </div>
          );
        })}
      </div>
      {fp.files.length > 0 && (
        <details className="rounded-xl border border-white/10 bg-black/30 p-4">
          <summary className="cursor-pointer font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9C9C2]">Stored files to be scrubbed ({fp.files.length})</summary>
          <ul className="mt-3 space-y-1">{fp.files.map((f) => <li key={f} className="font-body text-xs text-[#8A8A82] break-all" style={{ fontFamily: MONO }}>{f}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

function DeletionReceipt({ entry, onReset }) {
  return (
    <div className="rounded-2xl border border-[#2FB999]/40 bg-[#2FB999]/[0.05] p-6 md:p-8 space-y-5" data-testid="dg-receipt">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="w-6 h-6 text-[#2FB999]" />
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.26em] text-[#2FB999]">Hard delete sealed</div>
          <div className="font-serif text-2xl text-[#F5F5F0]" style={{ fontFamily: undefined }} data-testid="dg-receipt-ref">{entry.ref}</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 font-body text-sm text-[#C9C9C2]">
        <div><span className="text-[#8A8A82]">Deleted · </span>{entry.user_name ? `${entry.user_name} · ` : ""}{entry.user_email}</div>
        <div><span className="text-[#8A8A82]">When (IST) · </span>{entry.deleted_at_ist}</div>
        <div><span className="text-[#8A8A82]">Reason · </span>{entry.reason}{entry.reason_note ? ` — ${entry.reason_note}` : ""}</div>
        <div><span className="text-[#8A8A82]">Removed · </span>{entry.records_deleted} records · {entry.files_scrubbed} files scrubbed</div>
      </div>
      <div className="flex flex-wrap gap-3">
        <a data-testid="dg-receipt-letter" href={`${API}${entry.letter_url}`} className="inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-5 py-2.5 font-grotesk text-xs uppercase tracking-[0.22em] hover:bg-[#C9A961] transition-colors">
          <FileDown className="w-4 h-4" /> Download Letter of Intimation
        </a>
        <button data-testid="dg-receipt-new" onClick={onReset} className="rounded-full border border-white/20 px-5 py-2.5 font-grotesk text-xs uppercase tracking-[0.22em] text-[#C9C9C2] hover:border-[#B87333]">New search</button>
      </div>
    </div>
  );
}

function AuditLedger({ api, refreshKey }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData((await api.get("/admin/data-governance/audit")).data); }
    catch (e) { toast.error("Could not load audit ledger"); }
  }, [api]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="space-y-4" data-testid="dg-audit-ledger">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961] flex items-center gap-2"><ScrollText className="w-3.5 h-3.5" /> Permanent audit ledger</div>
          <h3 className="font-serif text-xl text-[#F5F5F0] mt-1">Every hard delete ever performed</h3>
        </div>
        {data && (
          <span data-testid="dg-chain-status" className="inline-flex items-center gap-2 rounded-full px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.22em]"
                style={data.chain_valid ? { color: "#2FE4C6", boxShadow: "inset 0 0 0 1px rgba(47,228,198,0.45)" } : { color: "#FF6B70", boxShadow: "inset 0 0 0 1px rgba(229,72,77,0.55)" }}>
            <Database className="w-3 h-3" /> {data.chain_valid ? "Hash chain intact" : "Chain integrity breach"} · {data.total} entries
          </span>
        )}
      </div>
      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-white/[0.03]">
            <tr className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">
              <th className="px-4 py-3">Ref</th><th className="px-4 py-3">Deleted identity</th><th className="px-4 py-3">Date & time (IST)</th>
              <th className="px-4 py-3">Admin</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Removed</th><th className="px-4 py-3">Letter</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.06]">
            {(data?.entries || []).map((e) => (
              <tr key={e.id} data-testid={`dg-audit-row-${e.ref}`} className="font-body text-sm text-[#C9C9C2] hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-[#C9A961]" style={{ fontFamily: MONO, fontSize: 12 }}>{e.ref}</td>
                <td className="px-4 py-3"><div className="text-[#F5F5F0]">{e.user_name || "—"}</div><div className="text-xs text-[#8A8A82]">{e.user_email}</div></td>
                <td className="px-4 py-3 whitespace-nowrap">{e.deleted_at_ist}</td>
                <td className="px-4 py-3 text-xs">{e.admin_email}</td>
                <td className="px-4 py-3"><div>{e.reason}</div>{e.reason_note && <div className="text-xs text-[#8A8A82]">{e.reason_note}</div>}</td>
                <td className="px-4 py-3 text-xs whitespace-nowrap">{e.records_deleted} rec · {e.files_scrubbed} files</td>
                <td className="px-4 py-3">
                  <a data-testid={`dg-letter-${e.ref}`} href={`${API}${e.letter_url}`} className="inline-flex items-center gap-1.5 text-[#C9A961] hover:text-[#F5F5F0] font-grotesk text-[10px] uppercase tracking-[0.2em]"><FileDown className="w-3.5 h-3.5" /> PDF</a>
                </td>
              </tr>
            ))}
            {data && data.entries.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center font-body text-sm text-[#5F5F58]" data-testid="dg-audit-empty">No hard deletes recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="font-body text-xs text-[#5F5F58]">This ledger is append-only and hash-chained. Entries can never be edited or deleted.</p>
    </div>
  );
}
